import { useMemo } from 'react';
import { Flex, Text, Pill, Box, Avatar } from '@infotrack/zenith-ui';
import {
  containerRowStyles,
  avatarSectionStyles,
  progressSectionStyles,
  progressTextStyles,
} from './topFiveTeamMembers.styles';
import { Progress } from '../../../shared/components/progress/progress';
import { Colors } from '@infotrack/zenith-ui/core/components/theme/colors';
import { getStatusInfo } from '../utils/statusUtils';
import type {
  ITopFiveApiV2EndpointsTopFiveSharedModelsStaffTopFiveDto,
  ITopFiveApiV2EndpointsTopFiveSharedModelsTopFiveItemDto,
} from '@/api/models';

type TopFiveTeamMemberRowProps = {
  staffItem: ITopFiveApiV2EndpointsTopFiveSharedModelsStaffTopFiveDto;
  currentItemName: string;
};

const findMatchingTopFiveItem = (
  staffItem: ITopFiveApiV2EndpointsTopFiveSharedModelsStaffTopFiveDto,
  currentItemName: string
): ITopFiveApiV2EndpointsTopFiveSharedModelsTopFiveItemDto | null => {
  if (!staffItem.topFives) return null;

  for (const topFive of staffItem.topFives) {
    if (!topFive.topFiveItems) continue;

    const matchingItem = topFive.topFiveItems.find(
      item => item.name?.toLowerCase() === currentItemName.toLowerCase()
    );

    if (matchingItem) return matchingItem;
  }

  return null;
};

export const TopFiveTeamMemberRow = ({
  staffItem,
  currentItemName,
}: TopFiveTeamMemberRowProps) => {
  const { staff } = staffItem;

  const matchingItem = useMemo(
    () => findMatchingTopFiveItem(staffItem, currentItemName),
    [staffItem, currentItemName]
  );

  const statusInfo = useMemo(
    () => getStatusInfo(matchingItem?.status, matchingItem?.result),
    [matchingItem?.status, matchingItem?.result]
  );

  const progressValue = useMemo(
    () => Math.max(0, Math.min(100, matchingItem?.completion || 0)),
    [matchingItem?.completion]
  );

  const displayName = useMemo(() => {
    return (
      staff?.fullName ||
      `${staff?.firstName || ''} ${staff?.surname || ''}`.trim() ||
      'Unknown'
    );
  }, [staff]);

  return (
    <Flex sx={containerRowStyles}>
      {/* Avatar + Name Section */}
      <Flex sx={avatarSectionStyles}>
        <Avatar name={displayName} />
        <Box sx={{ fontSize: ['0.875rem', '1rem'] }}>
          <Text variant="medium" color="primary/600">
            {displayName}
          </Text>
        </Box>
      </Flex>

      {/* Weight Pill */}
      <Pill
        variant="static"
        iconProps={{ name: 'weight', variant: 'outlined' }}
      >
        {`${matchingItem?.weight ?? 0}%`}
      </Pill>

      {/* Progress Section */}
      <Flex sx={progressSectionStyles}>
        <Flex sx={{ flex: 1 }}>
          <Progress
            color={statusInfo.color}
            progressValue={progressValue}
            isInteractive={false}
          />
        </Flex>
        <Box sx={{ ...progressTextStyles }}>
          <Text variant="medium" color="primary/600">
            {progressValue}%
          </Text>
        </Box>
      </Flex>

      {/* Status/Result Pill */}
      <Pill variant="static" background={statusInfo.pillBgColor as Colors}>
        {statusInfo.text}
      </Pill>
    </Flex>
  );
};
